import { Controller } from "@hotwired/stimulus"
import { htmlEscape } from "lib/helpers"

// stats--api on `app/views/stats/api_stats.haml`.
export default class extends Controller {
  static targets = [
    "graphDataButton",
    "graphTypeButton",
    "modulesChart",
    "apiSourcesChart",
    "platformSelect",
    "versionSelect",
    "datepicker",
    "callersChart",
    "accountDetails",
  ]

  static values = {
    firstStatDaysAgo: { type: Number, default: 0 },
    lastStatDaysAgo: { type: Number, default: 0 },
    graphUrl: { type: String, default: "/stats/global/api_graph" },
    callersUrl: { type: String, default: "/stats/top" },
    accountInfoPath: { type: String, default: "/stats/account_info" },
  }

  connect() {
    this.graphData = $.cookie("graph_data") || "all"
    this.graphType = $.cookie("graph_type") || "area"
    $.cookie("graph_data", this.graphData)
    $.cookie("graph_type", this.graphType)

    this.#syncSwitcherState()
    this.#initDatepicker()
    this.#initModulesChart()
    this.#initApiSourcesChart()
    this.#initCallersChart()
  }

  disconnect() {
    if (this.modulesChart) {
      this.modulesChart.destroy()
      this.modulesChart = null
    }
    if (this.apiSourcesChart) {
      this.apiSourcesChart.destroy()
      this.apiSourcesChart = null
    }
    if (this.callersChart) {
      this.callersChart.destroy()
      this.callersChart = null
    }
    if (this.hasDatepickerTarget && $(this.datepickerTarget).data("datepicker")) {
      $(this.datepickerTarget).off(".statsApi").datepicker("destroy")
    }
    if (this.hasAccountDetailsTarget && $(this.accountDetailsTarget).hasClass("ui-dialog-content")) {
      $(this.accountDetailsTarget).dialog("destroy")
    }
  }

  changePlatform(event) {
    this.#showSdkByPlatform(event.currentTarget.value, true)
  }

  changeVersion(event) {
    let platformVersion = event.currentTarget.value
    if (!platformVersion && this.hasPlatformSelectTarget) {
      platformVersion = this.platformSelectTarget.selectedOptions[0]?.text
    }
    this.#showSdkByPlatform(platformVersion, false)
  }

  changeGraphType(event) {
    this.graphType = event.currentTarget.value
    $.cookie("graph_type", this.graphType)
    this.#activateButton(this.graphTypeButtonTargets, event.currentTarget)
    this.#changeType(this.modulesChart.series, this.graphType)
    this.#changeType(this.apiSourcesChart.series, this.graphType)
  }

  changeGraphData(event) {
    this.graphData = event.currentTarget.value
    $.cookie("graph_data", this.graphData)
    this.#activateButton(this.graphDataButtonTargets, event.currentTarget)
    this.#clearSeries(this.modulesChart)
    this.#clearSeries(this.apiSourcesChart)
    this.modulesChart.showLoading()
    this.apiSourcesChart.showLoading()
    this.#requestData()
  }

  showAccountInfo(event) {
    event.preventDefault()
    const accountId = event.currentTarget.dataset.accountId
    if (!accountId || !this.hasAccountDetailsTarget) return

    $.get(`${this.accountInfoPathValue}/${accountId}`, (data) => {
      this.accountDetailsTarget.innerHTML = data
      $(this.accountDetailsTarget).dialog({
        position: "center",
        modal: true,
        width: 340,
        title: `Account #${accountId}`,
        draggable: false,
        resizable: false,
        close() {
          $(this).dialog("destroy")
        },
      })
    })
  }

  #initDatepicker() {
    if (!this.hasDatepickerTarget) return

    $(this.datepickerTarget)
      .datepicker({
        startDate: `-${this.firstStatDaysAgoValue}d`,
        endDate: `-${this.lastStatDaysAgoValue}d`,
      })
      .on("changeDate.statsApi", () => {
        this.#getCallersData($(this.datepickerTarget).datepicker("getDate"))
      })
  }

  #initModulesChart() {
    this.modulesChart = new Highcharts.Chart({
      chart: {
        renderTo: this.modulesChartTarget,
        type: this.graphType,
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
      },
      title: {
        text: "QB Modules usage",
      },
      xAxis: {
        type: "datetime",
        tickmarkPlacement: "on",
        title: {
          enabled: false,
        },
      },
      yAxis: {
        title: {
          text: "Percent",
        },
      },
      tooltip: {
        formatter() {
          let str = `${this.series.name}: ${Highcharts.numberFormat(this.y, 0, ",")} calls`
          if (this.percentage > 0) {
            str += ` (${Highcharts.numberFormat(this.percentage, 1)}%)`
          }
          return str
        },
      },
      plotOptions: {
        area: {
          stacking: "percent",
          lineColor: "#ffffff",
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: "#ffffff",
          },
        },
      },
      series: [],
    })
    this.modulesChart.showLoading()
  }

  #initApiSourcesChart() {
    const controller = this

    this.apiSourcesChart = new Highcharts.Chart({
      chart: {
        renderTo: this.apiSourcesChartTarget,
        type: this.graphType,
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        events: {
          load() {
            controller.#requestData()
          },
        },
      },
      title: {
        text: "QB API",
      },
      xAxis: {
        type: "datetime",
        tickmarkPlacement: "on",
        title: {
          enabled: false,
        },
      },
      yAxis: {
        title: {
          text: "Percent",
        },
      },
      tooltip: {
        formatter() {
          let str = `${this.series.name}: ${Highcharts.numberFormat(this.y, 0, ",")} calls`
          if (this.percentage > 0) {
            str += ` (${Highcharts.numberFormat(this.percentage, 1)}%)`
          }
          return str
        },
      },
      plotOptions: {
        area: {
          stacking: "percent",
          lineColor: "#ffffff",
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: "#ffffff",
          },
        },
      },
      series: [],
    })
    this.apiSourcesChart.showLoading()
  }

  #initCallersChart() {
    const controller = this

    this.callersChart = new Highcharts.Chart({
      chart: {
        renderTo: this.callersChartTarget,
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        events: {
          load() {
            controller.#getCallersData()
          },
        },
      },
      title: {
        text: "Top callers",
      },
      tooltip: {
        useHTML: true,
        formatter() {
          let hint = `<b>${htmlEscape(this.point.name)}</b>: ${this.point.config[1]} (${this.percentage.toFixed(2)}%)`
          if (this.point.config[2] != undefined) {
            hint += `<br><a href="#" data-account-id="${this.point.config[2]}" data-action="click->stats--api#showAccountInfo">view account</a>`
          }
          return hint
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
          },
          showInLegend: false,
        },
      },
      series: [
        {
          type: "pie",
          name: "API",
          center: [270, 180],
          data: [],
        },
      ],
    })
  }

  #requestData() {
    $.ajax({
      url: `${this.graphUrlValue}?data=${this.graphData}&get_module=true`,
      success: (dataArr) => {
        if (!this.modulesChart) return

        dataArr.modules_stat.forEach((retData) => {
          this.modulesChart.addSeries(
            {
              name: retData.serie_name,
              type: this.graphType,
              data: retData.data,
            },
            false,
          )
        })

        this.modulesChart.redraw()
        this.modulesChart.hideLoading()
      },
      cache: false,
    })

    $.ajax({
      url: `${this.graphUrlValue}?data=${this.graphData}&get_sdk=true`,
      success: (dataArr) => {
        if (!this.apiSourcesChart) return

        dataArr.sdks_stat.forEach((retData) => {
          this.apiSourcesChart.addSeries(
            {
              name: retData.serie_name,
              type: this.graphType,
              data: retData.data,
            },
            false,
          )
        })

        this.apiSourcesChart.redraw()
        this.apiSourcesChart.hideLoading()
      },
      cache: false,
    })
  }

  #showSdkByPlatform(platform, changeVersion) {
    this.apiSourcesChart.showLoading()
    $.ajax({
      url: `${this.graphUrlValue}?data=${this.graphData}&filter=${platform}&get_sdk=true`,
      success: (dataArr) => {
        if (!this.apiSourcesChart) return

        this.#setPlatformVersion(dataArr.versions, changeVersion)
        this.#clearSeries(this.apiSourcesChart)
        dataArr.sdks_stat.forEach((retData) => {
          this.apiSourcesChart.addSeries(
            {
              name: retData.serie_name,
              type: this.graphType,
              data: retData.data,
            },
            false,
          )
        })
        this.apiSourcesChart.redraw()
        this.apiSourcesChart.hideLoading()
      },
      cache: false,
    })
  }

  #getCallersData(dateText = "last") {
    let requestDate = dateText
    if (requestDate?.toString() === "[object Object]" || requestDate == undefined) {
      requestDate = "last"
    }

    const requestUrl = `${this.callersUrlValue}/${requestDate === "last" ? "last" : Date.parse(requestDate)}`
    $.ajax({
      url: requestUrl,
      success: (dataArr) => {
        if (!this.callersChart) return

        this.callersChart.series[0].setData([], false)
        dataArr.top_callers.forEach((data) => {
          this.callersChart.series[0].addPoint(
            [data.application_title, data.count, data.account_id],
            true,
          )
        })
        this.callersChart.hideLoading()
      },
      cache: false,
    })
  }

  #changeType(series, newType) {
    for (let i = 0; i < series.length; i++) {
      const serie = series[0]
      serie.chart.addSeries(
        {
          type: newType,
          name: serie.name,
          color: serie.color,
          data: serie.options.data,
        },
        false,
      )

      serie.remove()
    }
  }

  #clearSeries(chart) {
    while (chart.series.length > 0) {
      chart.series[0].remove(true)
    }
  }

  #setPlatformVersion(value, changeVersion) {
    if (changeVersion === false || !this.hasVersionSelectTarget) return

    $(this.versionSelectTarget).empty()
    $(this.versionSelectTarget).append($("<option>All</option>").attr("value", ""))
    if (value) {
      value.forEach((version) => {
        $(this.versionSelectTarget).append(
          $("<option></option>").attr("value", version).text(version),
        )
      })
    }
  }

  #syncSwitcherState() {
    this.graphDataButtonTargets.forEach((button) => {
      button.classList.toggle("active", button.value === this.graphData)
    })
    this.graphTypeButtonTargets.forEach((button) => {
      button.classList.toggle("active", button.value === this.graphType)
    })
  }

  #activateButton(buttons, activeButton) {
    buttons.forEach((button) => button.classList.toggle("active", button === activeButton))
  }
}
